import { useEffect, useRef, useState } from 'react'

import '../_menu.scss'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'

import user_placeholder from 'assets/images/icons/user_placeholder.svg'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import ProfileImageModal from 'components/UI/Modal/ProfileImage/ProfileImageModal'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { useChangeAccount } from 'helpers/hooks'
import { getLinkedAccounts } from 'services/endpoints/settings/account/profileSettings'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { type IProfile } from 'types/interfaces/IProfile'

interface IAccountProps {
  id: string
  profileImg: string
  displayName: string
  username: string
  followerCount?: number
  friendCount?: number
  clickFn: () => void
  role?: string
  wallet_deposit?: number
}

const MenuProfileAccount = ({
  profileImg,
  displayName,
  username,
  followerCount,
  friendCount,
  clickFn
}: IAccountProps) => {
  const { t } = useTranslation()

  // control the image src locally so parent re-renders can't revert it
  const [imgSrc, setImgSrc] = useState(profileImg || '')
  useEffect(() => {
    setImgSrc(profileImg || '')
  }, [profileImg])

  return (
    <div className='menu__profile__stats__model__account'>
      <div className='menu__profile__stats__model__img__container'>
        {imgSrc ? (
          <div className='menu__profile__stats__model__img'>
            <img
              key={imgSrc}
              src={imgSrc}
              alt={t('profilePhoto')}
              onError={() => setImgSrc(user_placeholder)}
              onLoad={(e) => {
                if (!e.currentTarget.naturalWidth || !e.currentTarget.naturalHeight) {
                  setImgSrc(user_placeholder)
                }
              }}
            />
          </div>
        ) : (
          <div className='profileholder__profile__placeholder__model'>
            <img src={user_placeholder} alt={t('placeholder')} />
          </div>
        )}
      </div>

      <div className='menu__profile__stats__model__all__stats__container'>
        <p className='menu__profile__name menu__profile__stats__model__name'>
          {displayName}
        </p>
        <p
          className='menu__profile__stats__username menu__profile__stats__model__username'
          onClick={clickFn}
        >
          @{username}
        </p>
        <div className='menu__profile__stats__model__num__container'>
          <p className='menu__profile__stats__num'>
            <span>{followerCount}</span> {t('Fans')}
          </p>
          <p className='menu__profile__stats__num'>
            <span>{friendCount}</span> {t('friends')}
          </p>
        </div>
      </div>
    </div>
  )
}

interface MenuProfileHolderProps {
  id: string
  profileImg: string
  display_name: string
  role: string
  follower_count?: number
  friend_count?: number
  following_count?: number
  wallet_deposit?: number
  username: string
}

export const MenuProfileHolder = ({
  id,
  profileImg,
  display_name,
  role,
  follower_count,
  friend_count,
  wallet_deposit,
  username
}: MenuProfileHolderProps) => {
  const [modelMenuOpen, setModelMenuOpen] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { changeAccount } = useChangeAccount()
  const { addSecondaryModal, clearSecondaryModal } = useModalContext()
  const fallbackUserData = useUserProfile()
  const toggleRef = useRef<HTMLParagraphElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const shouldFetchLinked =
    role === 'model' && fallbackUserData?.linked_account === true

  const { data } = useQuery(
    ['linkedAccounts', { userId: id, shouldFetch: shouldFetchLinked }],
    getLinkedAccounts,
    {
      enabled: shouldFetchLinked,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  )

const changeAvatar = () => {
  addSecondaryModal(
    t('profilePhoto'),
    <ProfileImageModal
      closeNestedModal={clearSecondaryModal}
    />,
    false,
    false,
    'profile__photo__modal',
    'profile-image'
  )
}
  // close on outside click (but allow clicks to still do their normal thing)
  useEffect(() => {
    if (!modelMenuOpen) return

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null
      if (!target) return

      const clickedToggle = !!toggleRef.current && toggleRef.current.contains(target)
      const clickedDropdown = !!dropdownRef.current && dropdownRef.current.contains(target)

      if (clickedToggle || clickedDropdown) return

      setModelMenuOpen(false)
    }

    // capture makes it reliable even if inner components stop bubbling later
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [modelMenuOpen])


  // robust pattern for the main avatar
  const [mainImgSrc, setMainImgSrc] = useState(profileImg || '')
  useEffect(() => {
    setMainImgSrc(profileImg || '')
  }, [profileImg])

  // add placeholder modifier ONLY for MenuProfileHolder when placeholder is shown
  const isPlaceholder = !mainImgSrc || mainImgSrc === user_placeholder

return (
  <>
    <div className='menu__profile'>
      <ImgInCircle
        id={id}
        type='profile'
        hasLoader={true}
        customClass={`menu__profile__iic ${isPlaceholder ? 'menu__profile__iic--placeholder' : ''}`}
        hasCamera={true}
        cameraFn={changeAvatar}
      >
        {mainImgSrc ? (
          <img
            key={mainImgSrc}
            src={mainImgSrc}
            alt={t('profilePhoto')}
            onError={() => setMainImgSrc(user_placeholder)}
            onLoad={(e) => {
              if (!e.currentTarget.naturalWidth || !e.currentTarget.naturalHeight) {
                setMainImgSrc(user_placeholder)
              }
            }}
          />
        ) : (
          <div className='profileholder__profile__placeholder'>
            <img src={user_placeholder} alt={t('placeholder')} />
          </div>
        )}
      </ImgInCircle>

      <div>
      <p
        ref={toggleRef}
        className={`menu__profile__name ${'menu__profile__name__model'}`}
        onClick={() => {
          setModelMenuOpen(!modelMenuOpen)
        }}
      >
        {display_name}
        {role === 'model' && (
          <span
            className={`menu__profile__name__model__chevron__container ${
              modelMenuOpen && 'menu__profile__name__model__chevron__container__open'
            }`}
          >
            <img src={AllIcons.chevron_down} alt={t('icon')} />
            <img src={AllIcons.chevron_down_blue} alt={t('icon')} />
          </span>
        )}
      </p>
        {role === 'fan' && (
          <div className='menu__profile__info'>
            <div
              className='menu__profile__info__wallet'
              onClick={() => navigate('/settings/general/wallet')}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate('/settings/general/wallet')
                }
              }}
            >
              <span className='menu__profile__info__wallet__value'>
                ${(() => {
                  const amount = Number(wallet_deposit ?? 0)
                  return amount % 1 === 0 ? amount : amount.toFixed(2)
                })()}{' '}
              </span>
              <span className='menu__profile__info__wallet__text'>
                Balance |
              </span>
            </div>
            <div className='menu__profile__info__username'>@{username}</div>
          </div>
        )}

        <div
           ref={dropdownRef}
          className={`menu__profile__stats ${'menu__profile__stats__model'}  ${
            modelMenuOpen && 'menu__profile__stats__model_open'
          }`}
        >
          <>
            <div className='menu__profile__stats__model__list'>
              <MenuProfileAccount
                id={id}
                profileImg={profileImg}
                displayName={display_name}
                username={username}
                followerCount={follower_count}
                friendCount={friend_count}
                clickFn={() => {
                  navigate(`/${username}`)
                }}
              />
              {data &&
                !!data.length &&
                data.map((profile: IProfile, idx: number) => (
                  <MenuProfileAccount
                    key={idx}
                    id={profile.id}
                    profileImg={profile.cropped_avatar?.url || profile.avatar?.url}
                    displayName={profile.display_name}
                    username={profile.username}
                    followerCount={profile.follower_count}
                    friendCount={profile.friend_count}
                    clickFn={() => {
                      changeAccount.mutate({ linked_id: profile.id })
                    }}
                  />
                ))}
            </div>
          </>
        </div>
      </div>
    </div>
  </>
  )
}

export default MenuProfileHolder
